<script setup lang="ts">
import { type ButtonColorVariant } from './types';
import { NuxtLink } from '#components';
import {
    type IconSize,
    type IconProp,
    type IconConfig,
    type ButtonType,
    type ButtonSize,
    type SizeConditional,
    useExtractSize,
} from '#imports';

const {
    leftIcon,
    iconButton,
    form = '',
    rightIcon,
    href = '',
    id,
    fullWidth,
    link,
    type = 'button',
    variant = 'filled',
    loading,
    size = 'big',
    noWrap,
    disablePrefetch,
    target = '_self',
    rel = '',
    disabled,
    color = 'primary',
    name = '',
} = defineProps<IProps>();

const emit = defineEmits<{
    click: [event: PointerEvent];
}>();

const attrs = useAttrs();

interface IProps {
    id?: string;
    variant?: ButtonType;
    size?: SizeConditional<ButtonSize>;
    disabled?: boolean;
    loading?: boolean;
    leftIcon?: IconProp;
    rightIcon?: IconProp;
    iconButton?: IconProp;
    fullWidth?: boolean;
    link?: boolean | 'nuxt';
    href?: string;
    target?: '_self' | '_blank' | '_parent' | '_top';
    disablePrefetch?: boolean;
    noWrap?: boolean;
    form?: string;
    type?: string;
    rel?: string;
    color?: ButtonColorVariant;
    name?: string;
}

const { mobile, desktop } = useExtractSize<ButtonSize>(() => size, 'big');

computed(() => {
    if (link && !href) {
        throw new Error('Link href not provided.');
    } else if (!link && href) {
        throw new Error(
            'href is not a valid property for button component. Did you forget `link` prop?'
        );
    } else if (link === 'nuxt' && target !== '_self') {
        throw new Error('Nuxt link only support opening in same tab');
    }
    return true;
});

const iconSize = computed<IconSize>(() => {
    if (iconButton) {
        switch (size) {
            case 'big':
                return 'large';
            case 'medium':
                return 'medium';
            case 'small':
                return 'small';
            default:
                return 'medium';
        }
    } else {
        switch (size) {
            case 'big':
                return 'medium';
            case 'medium':
                return 'small';
            case 'small':
                return 'x-small';
            default:
                return 'small';
        }
    }
});

const component = computed(() => {
    if (!link) {
        return 'button';
    } else {
        return link === 'nuxt' ? NuxtLink : 'a';
    }
});

const binds = computed(() => {
    const bind: Record<string, unknown> = { ...attrs };
    if (id) {
        bind.id = id;
    }
    if (link) {
        if (link === 'nuxt') {
            bind.to = href;
            bind.prefetch = !disablePrefetch;
            bind.rel = rel;
        } else {
            bind.href = href;
            bind.target = target;
            bind.rel = rel;
        }
    } else {
        bind.type = type || 'button';
        bind.form = form || undefined;
    }

    if (name) {
        bind['aria-label'] = name;
    }
    return bind;
});

const leftIconProps = computed(() => normalizeIcon(leftIcon));

const rightIconProps = computed(() => normalizeIcon(rightIcon));

const iconButtonProps = computed(() => normalizeIcon(iconButton));

const normalizeIcon = (icon?: IconProp) => {
    const result = {} as IconConfig;
    if (!icon) {
        return result;
    }
    if ('prefix' in icon) {
        result.icon = icon;
        result.pulse = undefined;
        result.color = undefined;
    } else {
        result.icon = icon.icon;
        result.pulse = icon.pulse;
        result.color = icon.color;
    }
    return result;
};

const native = ref<HTMLButtonElement | HTMLAnchorElement>();

defineExpose({
    native,
});
</script>

<template>
    <component
        :is="component"
        ref="native"
        class="mr-button"
        v-bind="binds"
        :class="[
            color,
            variant,
            `mobile-${mobile}`,
            desktop,
            {
                loading,
                'icon-button': iconButton,
                'full-width': fullWidth,
                'no-wrap': noWrap,
            },
        ]"
        :disabled
        @click="!loading && emit('click', $event)"
    >
        <mr-icon
            v-if="rightIconProps.icon"
            class="icon right"
            :icon="rightIconProps.icon"
            :color="rightIconProps.color"
            :size="iconSize"
            :pulse="rightIconProps.pulse"
        />
        <mr-icon
            v-if="iconButtonProps.icon"
            class="icon button"
            :icon="iconButtonProps.icon"
            :color="iconButtonProps.color"
            :size="iconSize"
            :pulse="iconButtonProps.pulse"
        />
        <mr-dots-loading
            v-if="loading"
            class="loading-component"
            :size="6"
            color="none"
            floating
        />
        <slot v-if="!iconButtonProps.icon" />
        <mr-icon
            v-if="leftIconProps.icon"
            class="icon left"
            :icon="leftIconProps.icon"
            :color="leftIconProps.color"
            :size="iconSize"
            :pulse="leftIconProps.pulse"
        />
    </component>
</template>

<style scoped lang="scss">
@mixin smallSize {
    @include tg-s($w7);
    height: 36px;
    min-height: 36px;
    padding: 5px 8px;
    &.icon-button {
        padding: 8px;
        width: 36px;
    }
}
@mixin mediumSize {
    @include tg-m($w7);
    height: 40px;
    min-height: 40px;
    padding: 8px;
    &.icon-button {
        padding: 8px;
        width: 40px;
    }
}
@mixin bigSize {
    @include tg-m($w7);
    height: 48px;
    min-height: 48px;
    padding: 10px 8px;
    &.icon-button {
        padding: 8px;
        width: 48px;
    }
}

@mixin colorVariant($color: $p-blue, $textColor: white) {
    &.transparent,
    &.bordered {
        background-color: transparent;
        color: usePalette($color, $p-400);
        .icon {
            color: usePalette($color, $p-400);
        }
        @include desktop-only() {
            &:not(:disabled):not(.loading):hover {
                background: rgba(
                    $color: usePalette($color, $p-500),
                    $alpha: 0.1
                );
                &.icon-button {
                    .icon {
                        color: usePalette($color, $p-600);
                    }
                    &.bordered {
                        border-color: usePalette($color, $p-600);
                    }
                }
            }
        }
        &:not(:disabled):not(.loading):active {
            color: usePalette($color, $p-600);
            background: rgba($color: usePalette($color, $p-500), $alpha: 0.3);
        }
        &:disabled {
            cursor: not-allowed;
            color: usePalette($p-gray, $p-600);
            .icon {
                color: usePalette($p-gray, $p-600);
            }
        }
        &.loading.bordered {
            border-color: usePalette($color, $p-300);
        }
        :deep(.loading-dot) {
            background: usePalette($color, $p-300) !important;
        }
    }
    &.bordered {
        border-width: 1px;
        border-style: solid;
        border-color: usePalette($color, $p-400);
        &:not(:disabled):not(.loading):active {
            border-color: usePalette($color, $p-600);
        }
        &:disabled {
            border-color: usePalette($p-gray, $p-600);
        }
    }
    &.filled {
        background: usePalette($color, $p-400);
        color: $textColor;
        .icon {
            color: $textColor;
        }

        @include desktop-only() {
            &:not(:disabled):not(.loading):hover {
                background: usePalette($color, $p-500);
            }
        }
        &:not(:disabled):not(.loading):active {
            background: usePalette($color, $p-600);
        }
        &:disabled {
            cursor: not-allowed;
            color: usePalette($p-gray, $p-600);
            background: usePalette($p-gray, $p-200);
            .icon {
                color: usePalette($p-gray, $p-600);
            }
        }
        :deep(.loading-dot) {
            background: $textColor !important;
        }
        &.loading {
            background-color: usePalette($color, $p-300);
        }
    }
}

// eslint-disable-next-line vue-scoped-css/require-selector-used-inside
.mr-button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 8px;
    border: 0;
    outline: none;
    cursor: pointer;
    overflow: hidden;
    box-sizing: border-box;
    // text
    @include set-font($important: true);
    @include fa-num;
    text-align: center;
    text-decoration: none;

    .icon {
        pointer-events: none;
    }

    &:hover {
        text-decoration: none;
        transition: all 200ms;
    }

    &.full-width {
        flex-shrink: unset;
        width: 100%;
    }

    &.no-wrap {
        flex-wrap: nowrap;
        flex-shrink: 0;
        white-space: nowrap;
    }

    &.loading {
        cursor: progress !important;
        color: transparent !important;
        .icon {
            visibility: hidden;
        }
    }

    // icon button
    &.icon-button {
        flex-shrink: 0;
        .icon {
            margin: 0 !important;
        }
    }

    // size
    &.big {
        @include bigSize();
    }
    &.medium {
        @include mediumSize();
    }
    &.small {
        @include smallSize();
    }
    @include mobile-only {
        // size
        &.mobile-big {
            @include bigSize();
        }
        &.mobile-medium {
            @include mediumSize();
        }
        &.mobile-small {
            @include smallSize();
        }
    }

    // type

    &.primary {
        @include colorVariant();
    }
    &.secondary {
        @include colorVariant($p-yellow, var(--yellow-900));
    }
}
</style>
